import { useAuth } from '@platform/auth-client';
import { Button, Dialog, formatNumber } from '@platform/design-system';
import { isLocale } from '@platform/i18n';
import type { TypedClient } from '@platform/openapi';
import { Download, FileSpreadsheet } from 'lucide-react';
import { useState } from 'react';
import { PII_READ_ROLE } from '../customers/customers.ts';
import { saveDownload } from '../saveDownload.ts';
import { usePageContext } from '../usePageContext.ts';
import { useOrderExport, useRequestOrderExport, type ExportLocale } from './order-export.ts';
import type { OrderNarrowingQuery } from './order-filters.ts';
import type { OrderView } from './orders.ts';
import styles from './Orders.module.css';

export type ExportScope = {
  readonly view: OrderView;
  readonly narrowing: OrderNarrowingQuery;
  readonly summary: string;
  readonly total: number;
};

export type ExportOrdersDialogProps = { api: TypedClient; scope: ExportScope; onClose: () => void };

const FALLBACK_LOCALE: ExportLocale = 'fr';

export const ExportOrdersDialog = ({ api, scope, onClose }: ExportOrdersDialogProps) => {
  const { t, locale, describeError } = usePageContext();
  const piiVisible = useAuth().hasRole(PII_READ_ROLE);
  const request = useRequestOrderExport(api);
  const [exportId, setExportId] = useState<string | null>(null);
  const followed = useOrderExport(api, exportId);
  const exported = followed.data;
  const fileLocale: ExportLocale = isLocale(locale) ? locale : FALLBACK_LOCALE;

  const prepare = () => {
    request.mutate(
      { view: scope.view, locale: fileLocale, ...scope.narrowing },
      { onSuccess: (requested) => setExportId(requested.id) },
    );
  };
  const download = async () => {
    const fresh = await followed.refetch();
    const url = fresh.data?.download?.url;
    if (url !== undefined) saveDownload(url);
  };
  const preparing =
    request.isPending ||
    (exportId !== null &&
      (exported === undefined || exported.status === 'queued' || exported.status === 'building'));

  const footer = () => {
    if (exported?.status === 'ready')
      return (
        <Button icon={<Download size={20} aria-hidden="true" />} onClick={() => void download()}>
          {t('orderExport.download')}
        </Button>
      );
    if (exported?.status === 'failed') return <Button onClick={prepare}>{t('orderExport.retry')}</Button>;
    return (
      <Button
        icon={<FileSpreadsheet size={20} aria-hidden="true" />}
        disabled={scope.total === 0 || preparing}
        onClick={prepare}
      >
        {preparing ? t('orderExport.preparing') : t('orderExport.prepare')}
      </Button>
    );
  };

  return (
    <Dialog open title={t('orderExport.title')} onClose={onClose} footer={footer()}>
      <div className={styles['exportBody']}>
        <p className={styles['exportScope']}>
          <strong>
            {t('orderExport.count', { count: scope.total, total: formatNumber(scope.total, locale) })}
          </strong>
          <span className={styles['muted']}>{scope.summary}</span>
        </p>
        <p className={styles['muted']}>{t('orderExport.format')}</p>
        {piiVisible ? null : <p className={styles['notice']}>{t('orderExport.masked')}</p>}
        <p role="status" aria-live="polite" className={styles['exportState']}>
          {preparing ? t('orderExport.building') : null}
          {exported?.status === 'ready'
            ? t('orderExport.ready', {
                count: exported.rowCount ?? 0,
                total: formatNumber(exported.rowCount ?? 0, locale),
              })
            : null}
          {exported?.status === 'failed' ? t('orderExport.failed') : null}
          {request.isError ? describeError(request.error) : null}
        </p>
      </div>
    </Dialog>
  );
};
